import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FilterAttributeOptionComponentDef } from '@df/catalog-ui/listing/filter/filter-attribute-option.component.def';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Model } from '@df/catalog/model';
import { Client } from '@df/core/service/client';
import { FacetUrlModule } from '@df/module-facet-url/facet-url.module';
import { NgInject } from '@df/ng/ng-inject';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ICmsConfigResource } from '@icms/core/resource/config/icms-config.resource';
import { NAttributeOrder } from 'projects/app-default/src/app/cms/config/attribute-order.cms-config';
import { CheckboxComponent } from 'projects/app-default/src/app/ui/form/checkbox';
import { SwatchComponent } from '../../../attribute/swatch/swatch.component';
import { NListing } from '../product-listing.interface';
import { FacetUrlPipe } from '@df/module-facet-url/util/facet-url.pipe';
@Component({
    selector: 'filter-attribute-option',
    templateUrl: './filter-attribute-option.html',
    styleUrl: './filter-attribute-option.scss',
    standalone: true,
    providers: [FacetUrlPipe],
    imports: [NgClass, GridModule, HrefComponent, FacetUrlModule, SwatchComponent, CheckboxComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterAttributeOptionComponent<T extends Model> extends FilterAttributeOptionComponentDef<T> implements NListing.IFilterAttr {
    client = inject(Client);

    private iCmsConfigResource = inject(ICmsConfigResource);
    private facetUrlPipe = inject(FacetUrlPipe);

    clear(event: Event, option: Catalog.IAttributeOption) {
        event.preventDefault();
        this.filter.unselect(option);
    }
    get zippyId() {
        return ['filter', this.filter.id];
    }
    getSelectedOptionsForFilter(lengthLimit = 20): string | undefined {
        if (this.filter) {
            const options = this.filter
                .getSelectedOptions()
                .map(i => i.label)
                .join(', ');
            this.detectChanges();
            return options.length > lengthLimit && options.indexOf(',', lengthLimit) !== -1
                ? options.slice(0, options.indexOf(',', lengthLimit)) + '...'
                : options;
        }
    }

    get isDefaultOpen(): boolean {
        return this.filter.isDefaultOpen;
    }

    get label(): string | undefined {
        return this.filter.attribute.data?.label;
    }

    get id(): string {
        return this.filter.config.id;
    }

    get isSwatch(): boolean {
        return !!this.filter.attribute.data?.isSwatch;
    }

    get isColorAttribute(): boolean {
        return this.filter.attribute.data?.code === 'color' || this.isSwatch;
    }

    get isSizeAttribute(): boolean {
        return this.filter.attribute.data?.code === 'size';
    }

    optionsWithMatches() {
        return this.filter.optionsWithMatches;
    }

    get hasAnyCurrentMatches(): boolean {
        return this.filter.hasAnyCurrentMatches;
    }

    getCurrentMatchesCount(option): number {
        return this.filter.getCurrentMatchesCount(option);
    }

    isSelected(option): boolean {
        return this.filter.isSelected(option);
    }

    toggle(option): void {
        this.filter.toggle(option);
    }

    get attrsOptionsOrder(): NAttributeOrder.IContent[] | undefined {
        return this.iCmsConfigResource?.getModelByUrl(NAttributeOrder.ID)?.data?.content?.attrs;
    }

    protected getOptionLink(option: Catalog.IAttributeOption): string | undefined {
        return this.facetUrlPipe.transform(option, this.filter);
    }

    protected sortedOptionsWithMatches() {
        const optionsWithMatches = this.filter.optionsWithMatches || [];

        const attrOptionsOrder = this.attrsOptionsOrder
            ?.find(e => e.attr === this.filter.attribute.id)
            ?.order?.map(i => i.optionLabel?.toLocaleLowerCase());
        if (attrOptionsOrder) {
            return optionsWithMatches.sort((a, b) => {
                const optionIndex1 = attrOptionsOrder.indexOf(
                    typeof a.label === 'string' ? a.label?.toLocaleLowerCase() : a.label.toString()
                );
                const optionIndex2 = attrOptionsOrder.indexOf(
                    typeof b.label === 'string' ? b.label?.toLocaleLowerCase() : b.label.toString()
                );
                return (optionIndex1 > -1 ? optionIndex1 : Infinity) - (optionIndex2 > -1 ? optionIndex2 : Infinity);
            });
        }
        return optionsWithMatches;
    }
}
